import { useState, useCallback, useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';

interface WalletPassResult {
  pass_url?: string;
  object_id?: string;
  vas_payload_b64?: string;
  error?: string;
  message?: string;
  diagnostic?: string;
}

async function extractInvokeErrorMessage(fnError: unknown): Promise<string | null> {
  const e = (fnError || {}) as {
    message?: string;
    context?: {
      clone?: () => { text?: () => Promise<string> };
    };
  };

  try {
    const text = await e.context?.clone?.().text?.();
    if (!text) return null;

    const parsed = JSON.parse(text) as { diagnostic?: string; message?: string; error?: string };
    return parsed.diagnostic || parsed.message || parsed.error || null;
  } catch {
    return null;
  }
}

function isLikelyIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const iDevice = /iPad|iPhone|iPod/.test(ua);
  const iPadDesktop = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  return iDevice || iPadDesktop;
}

function isAndroid(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Android/.test(navigator.userAgent);
}

function openApplePassBlob(blob: Blob, traceId: string): void {
  const objectUrl = URL.createObjectURL(blob);
  setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);

  if (isLikelyIOS()) {
    console.info('[WalletPass] Opening pkpass via location.href (iOS path)', { traceId });
    window.location.href = objectUrl;
    return;
  }

  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = 'ziplink-access.pkpass';
  a.rel = 'noopener';
  a.click();
}

export function useWalletPass() {
  const [isLoadingApple, setIsLoadingApple] = useState(false);
  const [isLoadingGoogle, setIsLoadingGoogle] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Cache the access token so iOS click handlers can build the URL
  // synchronously without breaking the user-gesture chain by awaiting.
  const tokenRef = useRef<string | null>(null);

  useEffect(() => {
    let active = true;
    const updateToken = async () => {
      const { data } = await supabase.auth.getSession();
      if (active) tokenRef.current = data.session?.access_token ?? null;
    };
    updateToken();
    const { data: subscription } = supabase.auth.onAuthStateChange((_event, session) => {
      tokenRef.current = session?.access_token ?? null;
    });
    return () => {
      active = false;
      subscription.subscription.unsubscribe();
    };
  }, []);

  const createTraceId = (action: 'apple' | 'google') => {
    const rnd = Math.random().toString(36).slice(2, 8);
    return `wallet-${action}-${Date.now()}-${rnd}`;
  };

  const generateApplePass = useCallback((keyId: string) => {
    setError(null);
    const traceId = createTraceId('apple');

    // iOS Safari: direct navigation for most reliable Wallet handoff.
    // CRITICAL: stay synchronous so the user-gesture chain isn't broken —
    // any await before window.location.href can cause iOS to silently block.
    if (isLikelyIOS()) {
      const token = tokenRef.current;
      if (!token) {
        setError('Inloggning krävs för att lägga till i Wallet');
        return;
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const endpoint = new URL(`${supabaseUrl}/functions/v1/api-wallet-pass`);
      endpoint.searchParams.set('action', 'apple');
      endpoint.searchParams.set('key_id', keyId);
      endpoint.searchParams.set('trace_id', traceId);
      endpoint.searchParams.set('delivery', 'binary');
      endpoint.searchParams.set('apikey', import.meta.env.VITE_SUPABASE_ANON_KEY);
      endpoint.searchParams.set('token', token);

      setIsLoadingApple(true);
      window.location.href = endpoint.toString();
      return;
    }

    // Non-iOS: async fetch + blob download
    setIsLoadingApple(true);
    void (async () => {
      try {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const { data: sessionData } = await supabase.auth.getSession();
        const token = sessionData.session?.access_token;

        const response = await fetch(`${supabaseUrl}/functions/v1/api-wallet-pass`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            action: 'apple',
            key_id: keyId,
            trace_id: traceId,
            delivery: 'binary',
          }),
        });

        const contentType = response.headers.get('content-type') || '';

        if (!response.ok) {
          const rawText = await response.text().catch(() => '');
          let readable = `Apple Wallet-förfrågan misslyckades (${response.status})`;
          try {
            const parsed = JSON.parse(rawText) as { error?: string; message?: string; diagnostic?: string };
            readable = parsed.diagnostic || parsed.message || parsed.error || readable;
          } catch { /* non-JSON */ }
          throw new Error(readable);
        }

        if (contentType.includes('application/vnd.apple.pkpass') || contentType.includes('application/octet-stream')) {
          const blob = await response.blob();
          openApplePassBlob(blob, traceId);
          return;
        }

        if (contentType.includes('application/json')) {
          const json = await response.json() as WalletPassResult;
          if (json.pass_url) {
            window.location.href = json.pass_url;
            return;
          }
          if (json.error) {
            setError(json.message || json.error);
            return;
          }
        }

        throw new Error('Oväntat svar från Apple Wallet.');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Kunde inte generera Apple Wallet-pass');
      } finally {
        setIsLoadingApple(false);
      }
    })();
  }, []);

  const generateGooglePass = useCallback(async (keyId: string) => {
    setIsLoadingGoogle(true);
    setError(null);
    const traceId = createTraceId('google');

    try {
      const { data, error: fnError } = await supabase.functions.invoke<WalletPassResult>('api-wallet-pass', {
        body: { action: 'google', key_id: keyId, trace_id: traceId },
      });

      if (fnError) {
        const detailed = await extractInvokeErrorMessage(fnError);
        throw new Error(detailed || fnError.message || 'Google Wallet-förfrågan misslyckades');
      }
      if (!data) throw new Error('Inget svar från servern');

      if (data.error) {
        setError(data.message || data.error);
        return;
      }

      if (data.pass_url) {
        window.open(data.pass_url, '_blank');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kunde inte generera Google Wallet-pass');
    } finally {
      setIsLoadingGoogle(false);
    }
  }, []);

  const clearError = useCallback(() => setError(null), []);

  return {
    generateApplePass,
    generateGooglePass,
    isLoadingApple,
    isLoadingGoogle,
    isLikelyIOS: isLikelyIOS(),
    isAndroid: isAndroid(),
    error,
    clearError,
  };
}
